import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GeneralDialogComponent } from '../../dialogs/components/general-dialog/general-dialog.component';
import { reloadDialogButton } from '../../dialogs/models/buttons.const';
import { GeneralDialog } from '../../dialogs/models/dialog.interface';

/** Per-tab marker recording when this tab last auto-reloaded to escape a stale bundle. */
const RELOAD_MARKER_KEY = 'pozi.stale-bundle.reloaded-at';

/**
 * A second chunk-load failure within this window means the reload did not help
 * (a genuinely broken deploy, or an intermediary still serving a stale
 * index.html) — stop reloading and tell the user instead.
 */
const RELOAD_LOOP_WINDOW_MS = 60_000;

/**
 * Recovers the tab when a lazy chunk cannot be loaded because the running bundle
 * predates the deployed one — every deploy replaces the whole `dist/`, so the
 * hashed chunks a stale `index.html` points at no longer exist (see the
 * index.html cache comment in nginx.conf).
 *
 * Reached from two places for the SAME failure — `withNavigationErrorHandler`
 * in app.config.ts and the global `StaleBundleErrorHandler`, because the router
 * re-throws after its handler runs — so `recover()` is idempotent by design.
 */
@Injectable({ providedIn: 'root' })
export class StaleBundleRecoveryService {
  private dialog = inject(MatDialog);

  /** Guards against the double notification described above. */
  private handled = false;

  recover(): void {
    if (this.handled) return;
    this.handled = true;

    if (this.canAutoReload()) {
      // `reload()` (unlike `location.replace(url)`) uses fetch cache mode
      // "reload", so index.html is always revalidated against the server —
      // this works even for a tab whose cached index.html is still fresh.
      window.location.reload();
      return;
    }

    this.openStaleBundleDialog();
  }

  /**
   * True at most once per {@link RELOAD_LOOP_WINDOW_MS} per tab. Returns false
   * when sessionStorage is unavailable (privacy modes, blocked storage): without
   * a marker a loop cannot be detected, and a dialog is safer than an
   * unbreakable reload cycle.
   */
  private canAutoReload(): boolean {
    try {
      const previous = Number(sessionStorage.getItem(RELOAD_MARKER_KEY));
      if (previous && Date.now() - previous < RELOAD_LOOP_WINDOW_MS) {
        return false;
      }

      sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  }

  private openStaleBundleDialog(): void {
    const data: GeneralDialog = {
      title: $localize`:@@dialog.error.stale-bundle.title:A new version is available`,
      text: $localize`:@@dialog.error.stale-bundle.text:This page could not finish loading because the application was updated. Reload to continue.`,
      isCloseButtonDisplayed: false,
      buttons: [reloadDialogButton],
    };

    this.dialog.open(GeneralDialogComponent, { data, disableClose: true });
  }
}
